import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Activity, Building2, Clock, Users, TrendingUp, TrendingDown } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

export default function Workspace() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/workspace/summary").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  if (data === false) return <div className="text-[#E24B4A]">You don't have access to workspace analytics.</div>;
  if (!data) return <div className="text-[#555568]">Loading workspace…</div>;

  if (data.anonymisation_blocked) {
    return (
      <div className="space-y-6" data-testid="workspace-anon-blocked">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">Workspace</p>
          <h1 className="font-display text-3xl text-[#1A1A2E] mt-1">Aggregates paused.</h1>
        </div>
        <section className="rounded-xl border border-[#BA7517]/30 bg-[#BA7517]/10 p-6">
          <p className="text-sm text-[#1A1A2E] font-semibold">{data.message}</p>
          <p className="text-xs text-[#555568] mt-2">
            Currently {data.users_count} member{data.users_count === 1 ? "" : "s"} active. Threshold: {data.k_anonymity_threshold}.
            We never compute or expose aggregate values that could re-identify an individual.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="workspace-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">Workspace</p>
        <h1 className="font-display text-3xl text-[#1A1A2E] mt-1">Aggregate, anonymised - by design.</h1>
        <p className="text-sm text-[#555568] mt-2">
          All workspace analytics are aggregated and anonymised. No individual user is ever identified by name in any chart or report.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Activity} label="Avg focus score" value={data.avg_focus_score} suffix="" />
        <Stat icon={Clock} label="Avg refocus" value={`${Math.floor(data.avg_refocus_seconds / 60)}m`} extra={`${data.avg_refocus_seconds % 60}s`} />
        <Stat icon={Users} label="Users" value={data.users_count} />
        <Stat icon={Building2} label="Hierarchy changes" value={data.hierarchy_change_events} />
      </div>

      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
        <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-5">Team rollup</h2>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.teams}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8ECF2" />
            <XAxis dataKey="name" stroke="#555568" fontSize={12} />
            <YAxis stroke="#555568" fontSize={12} />
            <Tooltip />
            <Bar dataKey="avg_focus" fill="#1A3A5C" radius={[4, 4, 0, 0]} />
            <Bar dataKey="face_events" fill="#BA7517" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </section>

      <section className="rounded-xl bg-[#1A3A5C] text-white p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#E8ECF2]/70 font-semibold mb-1">Note</p>
        <p className="text-sm text-[#E8ECF2]/85 max-w-3xl">
          As a {user?.role === "s_tier" ? "S-Tier" : "leader"}, you see {user?.org_tier?.toLowerCase() || "your tier"} and below.
          Hierarchy changes are surfaced as category-level rank changes only - individual identities are reserved for S-Tier audit.
        </p>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, suffix = "", extra = "" }) {
  return (
    <div className="rounded-xl border border-[#E8ECF2] bg-white p-5">
      <Icon size={16} className="text-[#2C6BAC]" />
      <p className="mt-3 font-display text-3xl font-bold text-[#1A1A2E] tabular-nums">{value}{suffix}{extra && <span className="text-base ml-1 text-[#555568]">{extra}</span>}</p>
      <p className="text-xs text-[#555568] mt-1">{label}</p>
    </div>
  );
}
